import { useReducer, useCallback, useEffect, useRef } from "react";

export interface VideoCoverState {
  cover: string;
  isLoadingThumb: boolean;
  isEditing: boolean;
  showLowResFallbackWarning: boolean;
  originalTimestamp?: number;
  originalCoverType?: string;
}

export type VideoCoverAction =
  | { type: "SET_COVER"; payload: string }
  | { type: "SET_LOADING_THUMB"; payload: boolean }
  | { type: "SET_EDITING"; payload: boolean }
  | { type: "SET_LOW_RES_FALLBACK_WARNING"; payload: boolean }
  | {
      type: "SET_ORIGINAL_VALUES";
      payload: { timestamp?: number; coverType?: string };
    }
  | { type: "RESET_STATE" };

const initialState: VideoCoverState = {
  cover: "",
  isLoadingThumb: false,
  isEditing: false,
  showLowResFallbackWarning: false,
  originalTimestamp: undefined,
  originalCoverType: undefined,
};

function videoCoverReducer(
  state: VideoCoverState,
  action: VideoCoverAction
): VideoCoverState {
  switch (action.type) {
    case "SET_COVER": {
      return { ...state, cover: action.payload };
    }
    case "SET_LOADING_THUMB": {
      return { ...state, isLoadingThumb: action.payload };
    }
    case "SET_EDITING": {
      return { ...state, isEditing: action.payload };
    }
    case "SET_LOW_RES_FALLBACK_WARNING": {
      return { ...state, showLowResFallbackWarning: action.payload };
    }
    case "SET_ORIGINAL_VALUES": {
      return {
        ...state,
        originalTimestamp: action.payload.timestamp ?? state.originalTimestamp,
        originalCoverType: action.payload.coverType ?? state.originalCoverType,
      };
    }
    case "RESET_STATE": {
      return initialState;
    }
    default: {
      return state;
    }
  }
}

export function useVideoCoverState(initialCover = "") {
  const [state, dispatch] = useReducer(videoCoverReducer, {
    ...initialState,
    cover: initialCover,
  });
  const previousInitialCoverRef = useRef(initialCover);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (initialCover === previousInitialCoverRef.current) {
      return;
    }

    const shouldSyncExternalCover =
      stateRef.current.cover === previousInitialCoverRef.current;

    previousInitialCoverRef.current = initialCover;

    if (shouldSyncExternalCover) {
      dispatch({ type: "SET_COVER", payload: initialCover });
    }
  }, [initialCover]);

  const setCover = useCallback(
    (cover: string) => dispatch({ type: "SET_COVER", payload: cover }),
    [dispatch]
  );

  const setIsLoadingThumb = useCallback(
    (loading: boolean) =>
      dispatch({ type: "SET_LOADING_THUMB", payload: loading }),
    [dispatch]
  );

  const setIsEditing = useCallback(
    (editing: boolean) => dispatch({ type: "SET_EDITING", payload: editing }),
    [dispatch]
  );

  const setShowLowResFallbackWarning = useCallback(
    (show: boolean) =>
      dispatch({ type: "SET_LOW_RES_FALLBACK_WARNING", payload: show }),
    [dispatch]
  );

  const setOriginalValues = useCallback(
    (values: { timestamp?: number; coverType?: string }) =>
      dispatch({ type: "SET_ORIGINAL_VALUES", payload: values }),
    [dispatch]
  );

  const resetState = useCallback(
    () => dispatch({ type: "RESET_STATE" }),
    [dispatch]
  );

  return {
    state,
    setCover,
    setIsLoadingThumb,
    setIsEditing,
    setShowLowResFallbackWarning,
    setOriginalValues,
    resetState,
  };
}
